import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { firebaseAuthService } from "@/../frontend/services/firebaseAuth.service";
import { qk } from "@/lib/lib/query/keys";
import { useToast } from "@/hooks/use-toast";
import { KnowledgeDoc } from "@/lib/lib/types";

export function useKnowledgeList(colonyId: string) {
  return useQuery({
    queryKey: qk.files(colonyId),
    queryFn: async () => {
      const user = await firebaseAuthService.getCurrentUser();
      const res: any = await api.get(`/api/colony/${colonyId}/knowledge`, { userId: user?.uid });
      return res.items as KnowledgeDoc[];
    },
    enabled: !!colonyId,
  });
}

export function useUploadKnowledge(colonyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File) => {
      const user = await firebaseAuthService.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      const mime = (file.type || 'application/octet-stream').toLowerCase();
      // Frontend guard: allow only plain text and markdown
      const allowedClientMimes = ['text/plain', 'text/markdown'];
      const allowedClientExts = ['.txt', '.md'];
      const lowerName = file.name.toLowerCase();
      const hasAllowedExt = allowedClientExts.some(ext => lowerName.endsWith(ext));
      if (!allowedClientMimes.includes(mime) && !hasAllowedExt) {
        const err = { error: 'unsupported_type', message: 'Only TXT and MD files are supported right now.' } as any;
        throw new Error(JSON.stringify(err));
      }
      const presign: any = await api.post(`/api/colony/${colonyId}/knowledge/presign`, {
        userId: user.uid,
        filename: file.name,
        mime,
        size: file.size
      });
      await fetch(presign.url, { method: 'PUT', body: file, headers: { 'Content-Type': mime } });
      const commit: any = await api.post(`/api/colony/${colonyId}/knowledge/commit`, {
        userId: user.uid,
        fileKey: presign.fileKey,
        title: file.name,
        mime,
        bytes: file.size
      });
      return commit.doc as KnowledgeDoc;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.files(colonyId) });
      toast({ title: "Uploaded", description: "File uploaded successfully" });
    },
    onError: (error: any) => {
      console.error("Upload error:", error);
      let description = "An unknown error occurred.";
      if (error && typeof error === 'object' && 'message' in error) {
        try {
          // The error message from the API client might be a JSON string
          const parsedError = JSON.parse(error.message);
          if (parsedError.error === 'unsupported_type') {
            description = parsedError.message || `File type is not supported.`;
          } else if (parsedError.error === 'file_too_large') {
            description = parsedError.message || 'The file is too large to upload.';
          } else {
            description = parsedError.message || String(error);
          }
        } catch (e) {
          // If parsing fails, use the raw message
          description = error.message || String(error);
        }
      } else {
        description = String(error);
      }
      toast({ title: "Upload failed", description, variant: "destructive" });
    },
  });
}

export function useDeleteKnowledge(colonyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (docId: string) => {
      const user = await firebaseAuthService.getCurrentUser();
      if (!user) throw new Error('Not authenticated');
      await api.delete(`/api/colony/${colonyId}/knowledge/${encodeURIComponent(docId)}`, { userId: user.uid });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.files(colonyId) });
      toast({ title: "Deleted", description: "File deleted successfully" });
    },
    onError: (error) => {
      console.error("Delete error:", error);
      toast({ title: "Delete failed", description: String(error), variant: "destructive" });
    },
  });
}